








































































































































import { Component, InjectReactive, Prop, Vue, Watch } from 'vue-property-decorator'
import { RealworksAPI } from '@/api/client/realworks'
import { InputSelectOption } from '@/helpers/options'
import { RealworksCredentials } from '@/api/model/credentials'
import { CredentialsType } from '@/api/types'
import { cloneDeep, isEqual } from 'lodash'
import { TriggeredReferral } from '@/api/model/triggered-referral'
import { knowledgeBaseUrl } from '@/helpers/knowledge-base'

@Component
export default class CreateAutoLeadFormStep1 extends Vue {
    @Prop({ required: true }) readonly value!: TriggeredReferral
    editingValue: TriggeredReferral = null
    @InjectReactive() loadingKenmerken!: boolean
    @InjectReactive() kenmerken!: InputSelectOption[]
    loadingAgendaTypes: boolean = true
    agendaTypes: InputSelectOption[] = []
    loadingAgendaStatussen: boolean = true
    agendaStatussen: InputSelectOption[] = []
    loadingTaakTypes: boolean = true
    taakTypes: InputSelectOption[] = []
    loadingTaakStatussen: boolean = true
    taakStatussen: InputSelectOption[] = []
    loadingRelatieTypes: boolean = true
    relatieTypes: InputSelectOption[] = []
    loading: boolean = false
    saving: boolean = false
    triggerType: string = null
    realworksCredentialId: string = null

    mounted() {
        this.reload()
    }

    reload() {
        this.loadAgendaTypes()
        this.loadAgendaStatussen()
        this.loadTaakTypes()
        this.loadTaakStatussen()
        this.loadRelatieTypes()
    }

    @Watch('triggerType')
    onTriggerTypeChanged(): void {
        if (this.triggerType !== 'kenmerken') {
            this.editingValue.realworksKenmerkId = null
        }

        if (this.triggerType !== 'agendaTypes') {
            this.editingValue.agendaTypes = []
            this.editingValue.agendaStatus = null
        }

        if (this.triggerType !== 'taakTypes') {
            this.editingValue.taakTypes = []
            this.editingValue.taakStatus = null
        }

        if (this.triggerType !== 'relatieTypes') {
            this.editingValue.relatieTypes = []
        }

        this.reload()
    }

    @Watch('value', { immediate: true })
    onValueChanged() {
        this.editingValue = cloneDeep(this.value)
        if (!this.triggerType) {
            this.triggerType = this.editingValue.triggerType
        }
        this.realworksCredentialId = this.editingValue.realworksCredentials?.id
    }

    @Watch('editingValue.realworksCredentials')
    @Watch('editingValue.realworksKenmerkId')
    @Watch('editingValue.agendaTypes')
    @Watch('editingValue.agendaStatus')
    @Watch('editingValue.taakTypes')
    @Watch('editingValue.taakStatus')
    @Watch('editingValue.relatieTypes')
    onEditingValueChanged() {
        if (isEqual(this.editingValue, this.value)) {
            return
        }

        this.$emit('update:value', this.editingValue)
    }

    @Watch('realworksCredentialId')
    onRealworksCredentialIdChanged() {
        this.editingValue.realworksCredentials = this.availableRealworksCredentials
            .find(c => c.id === this.realworksCredentialId)
    }

    async loadAgendaTypes() {
        this.loadingAgendaTypes = true

        const agendaTypes = await RealworksAPI.getAgendatypes()
        this.agendaTypes = agendaTypes
            .map(agendatype => ({
                label: agendatype,
                value: agendatype,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))

        this.loadingAgendaTypes = false
    }

    async loadAgendaStatussen() {
        this.loadingAgendaStatussen = true

        const agendaStatussen = await RealworksAPI.getAgendaStatussen()
        this.agendaStatussen = agendaStatussen
            .filter(status => status.actief)
            .sort((a, b) => a.volgorde > b.volgorde ? 1 : -1)
            .map(status => ({
                label: status.status,
                value: status.status,
            }))

        this.agendaStatussen.unshift({
            label: this.$t('forms.edit_realworks_status_rule_form.emptyAgendaStatus').toString(),
            value: null,
        })

        this.loadingAgendaStatussen = false
    }

    async loadTaakTypes() {
        this.loadingTaakTypes = true

        const taakTypes = await RealworksAPI.getTaaktypes()
        this.taakTypes = taakTypes
            .map(taak => ({
                label: taak,
                value: taak,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))

        this.loadingTaakTypes = false
    }

    async loadTaakStatussen() {
        this.loadingTaakStatussen = true

        const taakStatussen = await RealworksAPI.getTaakStatussen()
        this.taakStatussen = taakStatussen
            .filter(status => status.actief)
            .sort((a, b) => a.volgorde > b.volgorde ? 1 : -1)
            .map(status => ({
                label: status.status,
                value: status.status,
            }))

        this.taakStatussen.unshift({
            label: this.$t('forms.edit_realworks_status_rule_form.emptyTaakStatus').toString(),
            value: null,
        })

        this.loadingTaakStatussen = false
    }

    async loadRelatieTypes() {
        this.loadingRelatieTypes = true

        const relatieTypes = await RealworksAPI.getRelatietypes()
        this.relatieTypes = relatieTypes
            .map(relatietype => ({
                label: relatietype,
                value: relatietype,
            }))
            .sort((a, b) => a.label.localeCompare(b.label))

        this.loadingRelatieTypes = false
    }

    get helpUrl(): string {
        return knowledgeBaseUrl('realworksLeads')
    }

    get triggerTypeOptions() {
        return [
            { label: this.$t('company_settings.triggered_referrals_form.trigger-types.kenmerken'), value: 'kenmerken' },
            { label: this.$t('company_settings.triggered_referrals_form.trigger-types.agenda-types'), value: 'agendaTypes' },
            { label: this.$t('company_settings.triggered_referrals_form.trigger-types.taak-types'), value: 'taakTypes' },
            { label: this.$t('company_settings.triggered_referrals_form.trigger-types.relatie-types'), value: 'relatieTypes' },
        ]
    }

    get realworksCredentialOptions(): InputSelectOption[] {
        return this.availableRealworksCredentials
            .filter(realworksCredentials => {
                if (this.triggerType === 'agendaTypes') {
                    return realworksCredentials.canRequestAgenda
                }

                if (this.triggerType === 'taakTypes') {
                    return realworksCredentials.canRequestTaken
                }

                return realworksCredentials.canRequestRelaties
            })
            .map(realworksCredentials => ({
                label: realworksCredentials.description,
                value: realworksCredentials.id,
            }))
    }

    get availableRealworksCredentials(): RealworksCredentials[] {
        return this.$currentCompany
            .getCredentialsWithType<RealworksCredentials>(CredentialsType.REALWORKS)
            .sort((a, b) => a.description.localeCompare(b.description))
    }
}
